import { Add as AddIcon, PersonAdd as PersonAddIcon } from '@mui/icons-material';
import {
  Box,
  CircularProgress,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';

import { Context } from '../../service/Context';
import theme from '../../styles/theme';
import { formatClubTeamName, formatearNombre, ordenarPorCategoria } from '../../utils/UtilesComun';
import BarraBuscarJugador from '../buscadores/BarraBuscarJugador';
import { DialogApp, SelectWrapperApp } from '../custom/CustomComponents';

/**
 * Modal reutilizable para seleccionar un jugador mediante:
 * 1. Búsqueda global (BarraBuscarJugador)
 * 2. Desplegable por equipos del club y su plantilla
 */
const ModalSeleccionarJugador = ({
  open,
  onClose,
  onSelectJugador,
  title = 'Seleccionar jugador',
  equiposClub = null,
}) => {
  const { idClub, obtenerEquiposClub, obtenerPlantilla, obtenerHistoricoEquipo, equiposCache, setEquipoCacheItem } =
    useContext(Context);

  const [equipos, setEquipos] = useState(equiposClub || []);
  const [equipoSeleccionadoId, setEquipoSeleccionadoId] = useState(null);
  const [plantilla, setPlantilla] = useState([]);
  const [cargando, setCargando] = useState(false);

  // Sincronizar o cargar equipos del club
  useEffect(() => {
    if (equiposClub && equiposClub.length > 0) {
      setEquipos(equiposClub);
    } else if (open && idClub) {
      obtenerEquiposClub(idClub).then((res) => {
        if (res) {
          setEquipos(res);
        }
      });
    }
  }, [equiposClub, open, idClub, obtenerEquiposClub]);

  // Seleccionar por defecto el primer equipo ordenado por categoría
  useEffect(() => {
    if (equipos && equipos.length > 0) {
      const ordenados = ordenarPorCategoria(equipos);
      if (!equipoSeleccionadoId || !ordenados.some((eq) => eq.idEquipo === equipoSeleccionadoId)) {
        setEquipoSeleccionadoId(ordenados[0].idEquipo);
      }
    }
  }, [equipos, equipoSeleccionadoId]);

  // Cargar plantilla cuando cambia el equipo seleccionado
  useEffect(() => {
    let isMounted = true;
    const cargarPlantilla = async () => {
      if (!open || !equipoSeleccionadoId) {
        setPlantilla([]);
        return;
      }

      const cachedTeam = equiposCache?.[equipoSeleccionadoId];
      const cachedHistorico = cachedTeam?.historico;
      const idLigaCached = cachedHistorico && cachedHistorico.length > 0 ? cachedHistorico[0].idLiga : undefined;
      const cachedPlantilla = idLigaCached ? cachedTeam?.temporadas?.[idLigaCached]?.plantilla : null;

      if (cachedPlantilla && Array.isArray(cachedPlantilla) && cachedPlantilla.length > 0) {
        setPlantilla(cachedPlantilla);
        setCargando(false);
      } else {
        setPlantilla([]);
        setCargando(true);
      }

      try {
        let idLiga = idLigaCached;
        if (!idLiga) {
          const historico = await obtenerHistoricoEquipo(equipoSeleccionadoId);
          idLiga = historico && historico.length > 0 ? historico[0].idLiga : undefined;
          if (setEquipoCacheItem && historico) {
            setEquipoCacheItem(equipoSeleccionadoId, { historico });
          }
        }

        const res = await obtenerPlantilla(equipoSeleccionadoId, idLiga);
        if (isMounted) {
          const resList = res || [];
          setPlantilla(resList);
          if (setEquipoCacheItem && idLiga) {
            setEquipoCacheItem(equipoSeleccionadoId, {
              temporadas: {
                [idLiga]: {
                  plantilla: resList,
                },
              },
            });
          }
        }
      } catch (err) {
        console.error('Error al cargar plantilla del equipo:', err);
        if (isMounted && (!cachedPlantilla || cachedPlantilla.length === 0)) {
          setPlantilla([]);
        }
      } finally {
        if (isMounted) {
          setCargando(false);
        }
      }
    };

    cargarPlantilla();

    return () => {
      isMounted = false;
    };
  }, [open, equipoSeleccionadoId, obtenerHistoricoEquipo, obtenerPlantilla, equiposCache, setEquipoCacheItem]);

  const handleSelect = (jugador) => {
    if (!jugador) {
      return;
    }
    onSelectJugador(jugador);
    onClose();
  };

  return (
    <DialogApp open={open} onClose={onClose} title={title}>
      <Box>
        {/* 1. Búsqueda global */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle2" sx={{ color: theme.palette.text.secondary, display: 'block', mb: 1 }}>
            Búsqueda global
          </Typography>
          <Box sx={{ width: '100%' }}>
            <BarraBuscarJugador onSelectJugador={handleSelect} size="small" fullWidth />
          </Box>
        </Box>

        {/* Línea horizontal divisora */}
        <Box sx={{ borderBottom: `1px solid ${theme.palette.divider}`, my: 2 }} />

        {/* 2. Equipos del club */}
        {equipos && equipos.length > 0 && (
          <Box
            sx={{
              mb: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="subtitle2" sx={{ color: theme.palette.text.secondary, mb: 1, display: 'block' }}>
              Equipos del club
            </Typography>
            <SelectWrapperApp
              sx={{ width: '50%', alignItems: 'center', justifyContent: 'center' }}
              value={equipoSeleccionadoId || ''}
              onChange={(e) => setEquipoSeleccionadoId(e.target.value)}
              options={ordenarPorCategoria(equipos)}
              optionValue="idEquipo"
              renderLabel={(equipo) => formatClubTeamName(equipo.nombreEquipo, equipo.categoria)}
            />
          </Box>
        )}

        {/* Lista de jugadores de la plantilla */}
        {cargando ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : plantilla.length > 0 ? (
          <Box
            sx={{
              maxHeight: '50vh',
              overflowY: 'auto',
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              mt: 1.5,
            }}
          >
            {plantilla.map((jugInf) => (
              <ListItem
                key={jugInf.idJugador}
                onClick={() => handleSelect(jugInf)}
                sx={{
                  py: 0.5,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  cursor: 'pointer',
                  transition: 'background-color 0.15s ease',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <PersonAddIcon fontSize="small" color="primary" />
                </ListItemIcon>

                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {formatearNombre(jugInf.nombreJugador)}
                      </Typography>
                      {jugInf.ano && (
                        <Typography variant="caption" color="text.secondary">
                          {jugInf.ano}
                        </Typography>
                      )}
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelect(jugInf);
                    }}
                    color="primary"
                    size="small"
                  >
                    <AddIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </Box>
        ) : (
          equipos &&
          equipos.length > 0 && (
            <Box sx={{ textAlign: 'center', py: 2, color: 'text.secondary', fontSize: '0.82rem' }}>
              No hay jugadores disponibles en este equipo
            </Box>
          )
        )}
      </Box>
    </DialogApp>
  );
};

ModalSeleccionarJugador.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelectJugador: PropTypes.func.isRequired,
  title: PropTypes.string,
  equiposClub: PropTypes.array,
};

export default ModalSeleccionarJugador;
